/**
 * chatelles-cart-watcher.js — Chatelles
 *
 * Supprime automatiquement les accessoires orphelins quand
 * la chaussure parente est retirée du panier.
 *
 * Compatibilité : Savor / Horizon AI (Shopify)
 *
 * Installation :
 *   1. Uploader ce fichier dans assets/
 *   2. Ajouter dans layout/theme.liquid avant </body> :
 *      <script src="{{ 'chatelles-cart-watcher.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  async function fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('cart.js fetch failed');
    return res.json();
  }

  async function removeLine(lineKey) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
    if (!res.ok) throw new Error(`cart/change.js failed for ${lineKey}`);
    return res.json();
  }

  async function refreshSavorDrawer() {
    try {
      const cartData = await fetchCart();
      const { CartUpdateEvent } = await import('@theme/events');
      const event = new CartUpdateEvent(cartData, 'manual-trigger', {
        itemCount: cartData.item_count,
        source: 'chatelles-watcher',
        sections: {},
      });
      document.dispatchEvent(event);
    } catch {
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  let _running = false;

  async function removeOrphanAccessoires() {
    if (_running) return;
    _running = true;

    try {
      const cart = await fetchCart();
      const lines = cart.items || [];

      // Variantes "chaussures" = lignes sans propriété _variant_parent
      const parentIds = new Set(
        lines
          .filter(l => !l.properties || !l.properties['_variant_parent'])
          .map(l => String(l.variant_id))
      );

      // Accessoires orphelins = ceux dont le parent n'est plus dans le panier
      const orphans = lines.filter(l => {
        const pid = l.properties && l.properties['_variant_parent'];
        return pid && !parentIds.has(String(pid));
      });

      if (orphans.length === 0) return;

      console.info(`[Chatelles] ${orphans.length} accessoire(s) orphelin(s) détecté(s), suppression…`);

      for (const orphan of orphans) {
        try {
          await removeLine(orphan.key);
          console.info(`[Chatelles] Supprimé : ${orphan.title}`);
        } catch (e) {
          console.error(`[Chatelles] Erreur suppression "${orphan.title}":`, e);
        }
      }

      await refreshSavorDrawer();

    } catch (e) {
      console.error('[Chatelles cart-watcher] Erreur:', e);
    } finally {
      _running = false;
    }
  }

  function patchFetch() {
    const _orig = window.fetch;
    window.fetch = async function (...args) {
      const result = await _orig.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      // Déclencher après tout changement de panier initié par Savor.
      // Savor appelle routes.cart_change_url = "/cart/change" (SANS .js) →
      // le suffixe .js doit être optionnel, sinon on rate ses suppressions.
      if (/\/cart\/(change|update|clear)(\.js)?(\?|#|$)/.test(url)) {
        setTimeout(removeOrphanAccessoires, 600);
      }
      return result;
    };
  }

  function init() {
    patchFetch();
    // Écouter aussi les events natifs Savor. Savor émet "cart:update"
    // (ThemeEvents.cartUpdate, bubbles) lors d'une suppression/màj de ligne.
    ['cart:update', 'cart:change', 'cart:updated', 'line-item:change'].forEach(evt => {
      document.addEventListener(evt, () => setTimeout(removeOrphanAccessoires, 600));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();